import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useContacts } from '../contexts/ContactsContext';
import { useInvoices } from '../contexts/InvoicesContext';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';
const KEY = 'aidey_onboarding';

const STEPS = [
  {
    id: 'setup_profile',
    title: 'Set up your profile and preferences',
    desc: 'Add your name, timezone, and connect your calendar.',
    nav: 'Settings',
    navLabel: 'Go to Settings →',
    check: () => {
      try {
        const p = JSON.parse(localStorage.getItem('userProfile') || '{}');
        return !!(p.name || p.displayName);
      } catch { return false; }
    },
  },
  {
    id: 'add_task',
    title: 'Add your first task',
    desc: 'Create a task and let Aidey help you stay on top of your day.',
    nav: 'Tasks',
    navLabel: 'Go to Tasks →',
    check: (contacts, invoices, tasks) => tasks && tasks.length > 0,
  },
  {
    id: 'chat_aidey',
    title: 'Chat with Aidey',
    desc: 'Ask "What\'s on my agenda today?" or "Book a meeting tomorrow at 2pm".',
    nav: 'AideyChat',
    navLabel: 'Open Aidey Chat →',
    check: () => {
      try { return JSON.parse(localStorage.getItem('aidey_sessions') || '[]').length > 0; } catch { return false; }
    },
  },
  {
    id: 'add_contact',
    title: 'Add a contact',
    desc: 'Add a client or colleague to your contacts.',
    nav: 'Contacts',
    navLabel: 'Go to Contacts →',
    check: (contacts) => contacts.length > 0,
  },
  {
    id: 'create_invoice',
    title: 'Create your first invoice',
    desc: 'Create a draft invoice and send it to a client.',
    nav: 'Invoicing',
    navLabel: 'Go to Invoicing →',
    check: (contacts, invoices) => invoices.length > 0,
  },
  {
    id: 'setup_scheduling',
    title: 'Set up your scheduling page',
    desc: 'Create your public booking page and share your link with clients.',
    nav: 'Scheduling',
    navLabel: 'Go to Scheduling →',
    check: () => {
      try {
        return localStorage.getItem('aidey_sched_setup_done') === '1';
      } catch { return false; }
    },
  },
];

function loadProgress() {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
}
function saveProgress(p) {
  localStorage.setItem(KEY, JSON.stringify(p));
}

export default function OnboardingChecklist({ onNavigate, tasks = [] }) {
  const { t } = useTranslation('common');
  const { contacts } = useContacts();
  const { invoices } = useInvoices();
  const { currentUser } = useCurrentUser();
  const [progress, setProgress] = useState(loadProgress);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}').dismissed || false; } catch { return false; }
  });

  // Sync dismissed state from server preferences (survives cache clears / device switches)
  useEffect(() => {
    if (currentUser?.preferences?.onboardingDismissed && !dismissed) {
      setDismissed(true);
    }
  }, [currentUser?.preferences?.onboardingDismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  const recheck = useCallback(() => {
    const p = loadProgress();
    const updated = { ...p };
    for (const step of STEPS) {
      if (!updated[step.id] && step.check(contacts, invoices, tasks)) updated[step.id] = true;
    }
    saveProgress(updated);
    setProgress(updated);
  }, [contacts, invoices, tasks]);

  // Auto-check when contacts, invoices, or tasks change
  useEffect(() => { recheck(); }, [recheck]);

  // Real-time: re-check when any write site dispatches the progress event
  useEffect(() => {
    window.addEventListener('aidey:onboardingProgress', recheck);
    return () => window.removeEventListener('aidey:onboardingProgress', recheck);
  }, [recheck]);

  // Safety net: re-check on tab focus for multi-tab writes
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [recheck]);

  // Auto-dismiss 2 seconds after all steps are completed
  const completedCount = STEPS.filter(s => progress[s.id]).length;
  const allDone = completedCount === STEPS.length;

  function persistDismiss() {
    const updated = { ...loadProgress(), dismissed: true };
    saveProgress(updated);
    setDismissed(true);
    api('/api/users/me', {
      method: 'PUT',
      body: JSON.stringify({ preferences: { onboardingDismissed: true } }),
    }).catch(() => {});
  }

  useEffect(() => {
    if (allDone && !dismissed) {
      const t = setTimeout(persistDismiss, 2500);
      return () => clearTimeout(t);
    }
  }, [allDone, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  function markDone(stepId) {
    const updated = { ...progress, [stepId]: true };
    saveProgress(updated);
    setProgress(updated);
  }

  function dismiss() {
    persistDismiss();
  }

  if (dismissed) return null;

  const pct = Math.round((completedCount / STEPS.length) * 100);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #35454C',
      borderRadius: 12,
      marginBottom: '1.5rem',
      overflow: 'hidden',
      fontFamily: "'Raleway', sans-serif",
      boxShadow: '0 2px 8px rgba(12,45,72,0.06)',
    }}>
      {/* Header */}
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          borderBottom: collapsed ? 'none' : '1px solid #f0f0f0',
          cursor: 'pointer',
          background: allDone ? '#eaf6ef' : '#fff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          {/* Progress ring */}
          <div style={{ position: 'relative', width: 38, height: 38, flexShrink: 0 }}>
            <svg width="38" height="38" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="19" cy="19" r="15" fill="none" stroke="#f0f0f0" strokeWidth="3" />
              <circle
                cx="19" cy="19" r="15" fill="none"
                stroke={allDone ? '#27ae60' : NAVY} strokeWidth="3"
                strokeDasharray={`${2 * Math.PI * 15}`}
                strokeDashoffset={`${2 * Math.PI * 15 * (1 - pct / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.4s ease' }}
              />
            </svg>
            <span style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.65rem', fontWeight: 800, color: allDone ? '#27ae60' : NAVY,
              fontFamily: "'Poppins', sans-serif",
            }}>
              {completedCount}/{STEPS.length}
            </span>
          </div>

          <div>
            <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.95rem', color: NAVY }}>
              {allDone ? t('onboarding.all_done_title') : t('onboarding.get_started')}
            </div>
            <div style={{ fontSize: '0.77rem', color: '#aaa', marginTop: 1 }}>
              {allDone
                ? t('onboarding.closing')
                : STEPS.length - completedCount === 1
                  ? t('onboarding.steps_remaining_one')
                  : t('onboarding.steps_remaining_other', { count: STEPS.length - completedCount })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={e => { e.stopPropagation(); dismiss(); }}
            style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '0.75rem', fontFamily: "'Raleway', sans-serif", padding: '0.2rem 0.4rem' }}
          >
            {t('onboarding.dismiss')}
          </button>
          <span style={{ color: '#ccc', fontSize: '0.75rem' }}>{collapsed ? '▾' : '▴'}</span>
        </div>
      </div>

      {/* Steps */}
      {!collapsed && (
        <div>
          {STEPS.map((step, i) => {
            const done = !!progress[step.id];
            return (
              <div
                key={step.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.85rem',
                  padding: '0.8rem 1.25rem',
                  borderBottom: i < STEPS.length - 1 ? '1px solid #f5f5f5' : 'none',
                  opacity: done ? 0.55 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {/* Checkbox */}
                <button
                  onClick={() => !done && markDone(step.id)}
                  title={done ? 'Completed' : 'Mark as done'}
                  style={{
                    width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 2,
                    border: done ? `2px solid ${NAVY}` : '2px solid #d0d0d0',
                    background: done ? NAVY : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: done ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {done && <span style={{ color: YELLOW, fontSize: '0.65rem', fontWeight: 900 }}>✓</span>}
                </button>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    onClick={() => !done && onNavigate && onNavigate(step.nav)}
                    style={{
                      fontWeight: 700, fontSize: '0.88rem',
                      color: done ? '#aaa' : NAVY,
                      textDecoration: done ? 'line-through' : 'none',
                      marginBottom: '0.2rem',
                      fontFamily: "'Raleway', sans-serif",
                      cursor: !done && onNavigate ? 'pointer' : 'default',
                    }}
                  >
                    {step.title}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#999', lineHeight: 1.45, marginBottom: !done && onNavigate ? '0.4rem' : 0 }}>
                    {step.desc}
                  </div>
                  {!done && onNavigate && (
                    <button
                      onClick={() => onNavigate(step.nav)}
                      style={{
                        background: 'none', border: 'none', padding: 0,
                        fontSize: '0.76rem', fontWeight: 700,
                        color: NAVY, cursor: 'pointer',
                        fontFamily: "'Raleway', sans-serif",
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                      }}
                    >
                      {step.navLabel}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
